import { Button, Modal } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DocumentUploadList from './DocumentUploadList';
import { ProcessingScreen } from 'components/WorkOrder/WOScanProcessingScreen';
import { sortFilesByValidity } from 'shared/utils';
import * as Sentry from '@sentry/react';

const StyledModal = styled(Modal)`
  .ant-modal-header {
    margin-bottom: 30px;
    color: #02103d;
  }

  .ant-modal-title {
    color: #02103d;
  }

  .ant-modal-close-x {
    color: #02103d;
  }

  .loading-box {
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 5px;
    padding: 0.4rem;
    width: 100%;
    span {
      font-size: 14px;
      color: #02103d;
    }
  }
`;

const StyledP = styled.p`
  font-size: 14px;
`;

const DropZone = styled.div`
  border: 2px dashed ${(props) => (props.isDragOver ? '#1890ff' : '#d9d9d9')};
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  background-color: ${(props) => (props.isDragOver ? '#f0f8ff' : '#fafafa')};
  transition: all 0.3s ease;
  margin-bottom: 16px;

  &:hover {
    border-color: #1890ff;
    background-color: #f0f8ff;
  }
`;

const SFTPUploadDocModal = ({
  isVisible,
  onUpload,
  onCancel,
  isLoading,
  isError,
  uploading,
  currentStep,
}) => {
  const [fileList, setFileList] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [currentLoadingMsg, setCurrentLoadingMsg] = useState(
    'Hang tight, we are processing your files...'
  );

  const intervalRef = useRef(null);
  const globalErrorModalOpen = useRef(false);
  const hasResetFileList = useRef(true);

  const randomLoadingMessages = [
    'Hang tight, we are processing your files...',
    'Working on your files... almost there!',
  ];

  useEffect(() => {
    if (isVisible) {
      // Start loading message rotation
      intervalRef.current = setInterval(() => {
        setCurrentLoadingMsg(
          randomLoadingMessages[
            Math.floor(Math.random() * randomLoadingMessages.length)
          ]
        );
      }, 3000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => {
      clearInterval(intervalRef.current);
    };
  }, [isVisible, randomLoadingMessages]);

  useEffect(() => {
    if (isVisible && hasResetFileList.current) {
      setFileList([]);
      hasResetFileList.current = false;
    } else if (!isVisible) {
      hasResetFileList.current = true;
    }
  }, [isVisible]);

  const handleFileChange = ({ fileList: newFileList }) => {
    const { validFiles, oversizedFiles } = sortFilesByValidity(newFileList);

    if (oversizedFiles.length > 0 && !globalErrorModalOpen.current) {
      globalErrorModalOpen.current = true;

      const oversizedFileList = oversizedFiles
        .map(
          (file) =>
            `• ${file.name} (${(file.size / (1024 * 1024)).toFixed(1)}MB)`
        )
        .join('\n');
      Sentry.captureException(new Error('Files Too Large'), {
        extra: {
          oversizedFileList,
        },
      });
      Modal.error({
        title: 'Files Too Large',
        content: (
          <div>
            <p>
              The following files exceed the 12MB limit and cannot be uploaded:
            </p>
            <pre
              style={{
                backgroundColor: '#f5f5f5',
                padding: '10px',
                borderRadius: '4px',
                maxHeight: '200px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
              }}
            >
              {oversizedFileList}
            </pre>
            <p>Reduce the file sizes and try again.</p>
          </div>
        ),
        width: 500,
        onOk: () => {
          globalErrorModalOpen.current = false;
        },
        onCancel: () => {
          globalErrorModalOpen.current = false;
        },
      });
    }

    setFileList(validFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const droppedFiles = Array.from(e.dataTransfer.files);

    const validTypeFiles = droppedFiles.filter((file) => {
      const validTypes = ['.png', '.jpg', '.jpeg', '.pdf'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      return validTypes.includes(fileExtension);
    });
    const { validFiles, oversizedFiles } = sortFilesByValidity(validTypeFiles);
    if (oversizedFiles.length > 0 && !globalErrorModalOpen.current) {
      globalErrorModalOpen.current = true;

      const oversizedFileList = oversizedFiles
        .map(
          (file) =>
            `• ${file.name} (${(file.size / (1024 * 1024)).toFixed(1)}MB)`
        )
        .join('\n');
      Sentry.captureException(new Error('Files Too Large'), {
        extra: {
          oversizedFileList,
        },
      });
      Modal.error({
        title: 'Files Too Large',
        content: (
          <div>
            <p>
              The following files exceed the 12MB limit and cannot be uploaded:
            </p>
            <pre
              style={{
                backgroundColor: '#f5f5f5',
                padding: '10px',
                borderRadius: '4px',
                maxHeight: '200px',
                overflow: 'auto',
                whiteSpace: 'pre-wrap',
              }}
            >
              {oversizedFileList}
            </pre>
            <p>Reduce the file sizes and try again.</p>
          </div>
        ),
        width: 500,
        onOk: () => (globalErrorModalOpen.current = false),
        onCancel: () => (globalErrorModalOpen.current = false),
      });
    }

    if (validFiles.length > 0) {
      setFileList((prev) => [...prev, ...validFiles]);
    }
  };

  const uploadProps = {
    accept: '.png,.jpg,.jpeg,.pdf',
    onRemove: (file) => {
      setFileList((curr) => {
        const index = curr.indexOf(file);
        const newFileList = curr.slice();
        newFileList.splice(index, 1);
        return newFileList;
      });
    },
    beforeUpload: (file) => {
      setFileList((curr) => [...curr, file]);
      return false; // Prevent automatic uploading
    },
    fileList,
    onChange: handleFileChange,
    multiple: true,
  };

  return (
    <StyledModal
      title="AI-Powered Work Order Scanner"
      open={isVisible}
      onOk={() => onUpload(fileList)}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          loading={isLoading}
          disabled={fileList.length === 0}
          key="submit"
          type="primary"
          onClick={() => onUpload(fileList)}
        >
          {isLoading ? 'Processing' : 'Process'}
        </Button>,
      ]}
    >
      <StyledP style={{ marginTop: -10 }}>
        Upload multiple work order files at once. Pitstop's AI will scan and
        extract maintenance data from each file. Once scanning is complete, link
        each file to an existing work order in Pitstop.
        <br />
        To ensure the best results, follow these upload guidelines:
      </StyledP>
      <ul>
        <li>Supported formats are PNG, JPG, PDF (&lt; 12MB).</li>
        <li>Each work order must be a separate file.</li>
        <li>
          Clear, high‑res (&gt;250dpi) images work best; avoid shadows, skewed
          pages or large watermarks.
        </li>
        <li>Make sure the file name doesnt have any spaces and is unique.</li>
      </ul>

      <DropZone
        isDragOver={isDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragOver ? 'Drop files here' : 'Drag and drop files here'}
      </DropZone>

      <StyledP style={{ marginBottom: 0 }}>
        <b>Add Work Order Files:</b>
      </StyledP>
      <DocumentUploadList
        isDisabled={false}
        uploadProps={uploadProps}
        maxVisibleItems={5}
      />
      {uploading && (
        <div className="loading-box">
          <ProcessingScreen currentStep={currentStep} />
        </div>
      )}
      {isError && (
        <div className="loading-box">
          <span
            style={{
              color: 'red',
            }}
          >
            Something went wrong. Please try again later.
          </span>
        </div>
      )}
    </StyledModal>
  );
};

SFTPUploadDocModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onUpload: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  uploading: PropTypes.bool,
  // progress: PropTypes.number,
  currentStep: PropTypes.number,
};

SFTPUploadDocModal.defaultProps = {
  isLoading: false,
  isError: false,
  uploading: false,
  // progress: 0,
  currentStep: 0,
};

export default SFTPUploadDocModal;
